// Phase 38 — Institutional Risk Scoring Profiles
// These profiles DO NOT replace the main Bond Mentor signal. They explain how
// different risk styles may interpret the same confirmed market condition.

const PROFILE_DEFINITIONS = [
  {
    key: 'conservative',
    label: 'Conservative',
    shortLabel: 'Capital First',
    description: 'Protects capital first and reacts earlier to macro pressure, volatility, and credit stress.',
    weights: {
      yieldCurve: 0.18,
      creditSpreads: 0.22,
      fedPosture: 0.16,
      vigilanteActivity: 0.14,
      volatility: 0.16,
      liquidity: 0.10,
      moneyFlows: 0.02,
      globalBonds: 0.02,
    },
    bias: 4,
    macroSensitivity: 1.25,
    confidenceSensitivity: 0.08,
  },
  {
    key: 'balanced',
    label: 'Balanced',
    shortLabel: 'Default Lens',
    description: 'Follows the main Bond Mentor signal most closely and avoids overreacting to one noisy input.',
    weights: null,
    bias: 0,
    macroSensitivity: 0.55,
    confidenceSensitivity: 0.04,
  },
  {
    key: 'growth',
    label: 'Growth',
    shortLabel: 'Risk Tolerant',
    description: 'Accepts more short-term noise and waits for stronger confirmation before reducing exposure.',
    weights: {
      yieldCurve: 0.23,
      creditSpreads: 0.19,
      fedPosture: 0.13,
      vigilanteActivity: 0.12,
      volatility: 0.08,
      liquidity: 0.08,
      moneyFlows: 0.10,
      globalBonds: 0.07,
    },
    bias: -4,
    macroSensitivity: 0.35,
    confidenceSensitivity: 0.02,
  },
  {
    key: 'income',
    label: 'Income / Yield',
    shortLabel: 'Rate Watch',
    description: 'Focuses on whether yields, Fed policy, inflation events, and credit quality are helping or hurting income investors.',
    weights: {
      yieldCurve: 0.25,
      creditSpreads: 0.17,
      fedPosture: 0.25,
      vigilanteActivity: 0.10,
      volatility: 0.07,
      liquidity: 0.08,
      moneyFlows: 0.03,
      globalBonds: 0.05,
    },
    bias: 1,
    macroSensitivity: 1.0,
    confidenceSensitivity: 0.05,
  },
  {
    key: 'institutional',
    label: 'Institutional',
    shortLabel: 'Risk Desk',
    description: 'Looks for hidden risk beneath the headline signal: confirmation quality, liquidity, volatility, macro windows, and data confidence.',
    weights: {
      yieldCurve: 0.18,
      creditSpreads: 0.20,
      fedPosture: 0.16,
      vigilanteActivity: 0.14,
      volatility: 0.14,
      liquidity: 0.12,
      moneyFlows: 0.03,
      globalBonds: 0.03,
    },
    bias: 2,
    macroSensitivity: 1.1,
    confidenceSensitivity: 0.07,
  },
];

function clamp(value, min = 0, max = 100) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function round(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(digits));
}

function weightedScore(subSignals = {}, weights = null, fallbackScore = 50) {
  if (!weights) return Number(fallbackScore);

  let total = 0;
  let totalWeight = 0;
  Object.entries(weights).forEach(([key, weight]) => {
    const value = Number(subSignals[key]);
    if (Number.isFinite(value)) {
      total += value * weight;
      totalWeight += weight;
    }
  });

  if (!totalWeight) return Number(fallbackScore);
  return total / totalWeight;
}

function macroPressureScore(macroEvents = {}) {
  if (!macroEvents?.enabled) return 0;
  const text = [
    macroEvents.riskLabel,
    macroEvents.label,
    macroEvents.tone,
    macroEvents.windowLabel,
    macroEvents.summary,
  ].filter(Boolean).join(' ').toLowerCase();

  const events = Array.isArray(macroEvents.events) ? macroEvents.events : [];
  const activeEventCount = events.length;

  let score = activeEventCount ? 4 : 0;
  if (text.includes('event watch') || text.includes('major') || text.includes('high-impact')) score += 6;
  if (text.includes('caution') || text.includes('watch')) score += 4;
  if (text.includes('danger') || text.includes('emergency')) score += 10;
  if (text.includes('no major') || text.includes('none detected')) score = 0;

  return clamp(score, 0, 18);
}

function confidencePenalty(confidence = {}) {
  const label = String(confidence?.label || confidence?.level || '').toUpperCase();
  const score = Number(confidence?.score);
  if (Number.isFinite(score)) {
    if (score >= 85) return 0;
    if (score >= 65) return 3;
    if (score >= 45) return 8;
    return 14;
  }
  if (label === 'HIGH') return 0;
  if (label === 'MODERATE') return 3;
  if (label === 'LOW') return 8;
  if (label === 'VERY LOW') return 14;
  return 2;
}

function actionForScore(score, profileKey) {
  if (score <= 24) {
    if (profileKey === 'conservative' && score >= 22) return 'Stay invested, but avoid adding new risk until the next confirmation.';
    return 'Stay fully invested.';
  }
  if (score <= 42) return 'Tighten stops and avoid aggressive new positions.';
  if (score <= 55) return 'Reduce to roughly 75% risk exposure.';
  if (score <= 62) return 'Reduce closer to 50% risk exposure.';
  if (score <= 82) return 'Move defensive and prioritize capital preservation.';
  return 'Maximum defense — protect capital first.';
}

function statusForScore(score) {
  if (score <= 21) return { status: 'Stay Invested', tone: 'green', badge: 'Supportive' };
  if (score <= 29) return { status: 'Mild Watch', tone: 'blue', badge: 'Watch' };
  if (score <= 42) return { status: 'Caution', tone: 'yellow', badge: 'Caution' };
  if (score <= 55) return { status: 'Reduce Risk', tone: 'orange', badge: 'Elevated' };
  return { status: 'Defensive', tone: 'red', badge: 'High Risk' };
}

function topDrivers(subSignals = {}) {
  const labels = {
    yieldCurve: 'yield curve',
    creditSpreads: 'credit spreads',
    fedPosture: 'Fed policy',
    vigilanteActivity: 'Treasury pressure',
    volatility: 'volatility',
    liquidity: 'liquidity',
    moneyFlows: 'money flows',
    globalBonds: 'global bonds',
  };

  return Object.entries(subSignals)
    .map(([key, value]) => ({ key, label: labels[key] || key, value: Number(value) }))
    .filter((item) => Number.isFinite(item.value))
    .sort((a, b) => b.value - a.value)
    .slice(0, 2);
}

function buildReason(profile, profileScore, baseScore, subSignals, macroPressure, confidenceDrag) {
  const drivers = topDrivers(subSignals);
  const driverText = drivers.length
    ? `Biggest watch areas: ${drivers.map((d) => `${d.label} ${round(d.value)}/100`).join(', ')}.`
    : 'No single internal driver is dominating the profile.';

  const scoreDiff = round(profileScore - baseScore);
  const adjustmentText = scoreDiff > 2
    ? `This profile reads risk about ${scoreDiff} points higher than the main signal.`
    : scoreDiff < -2
      ? `This profile reads risk about ${Math.abs(scoreDiff)} points lower than the main signal.`
      : 'This profile is close to the main signal.';

  const macroText = macroPressure > 0
    ? 'A macro-event watch window adds monitoring pressure, but it does not override the confirmed signal by itself.'
    : 'No major macro-event pressure is changing this profile today.';

  const confidenceText = confidenceDrag > 0
    ? 'Data confidence adds a small caution buffer because some feeds may be delayed, estimated, or recovered.'
    : 'Data confidence is strong enough that no extra caution buffer is needed.';

  return `${adjustmentText} ${driverText} ${macroText} ${confidenceText}`;
}

export function computeInstitutionalRiskProfiles({ signal, macroEvents, confidence } = {}) {
  const baseScore = clamp(signal?.compositeScore ?? signal?.exactCompositeScore ?? 50);
  const subSignals = signal?.subSignals || {};
  const macroPressure = macroPressureScore(macroEvents);
  const dataConfidencePenalty = confidencePenalty(confidence);

  return PROFILE_DEFINITIONS.map((profile) => {
    const baseProfileScore = weightedScore(subSignals, profile.weights, baseScore);
    const adjustedScore = clamp(
      baseProfileScore
      + profile.bias
      + (macroPressure * profile.macroSensitivity)
      + (dataConfidencePenalty * profile.confidenceSensitivity)
    );
    const finalScore = Math.round(adjustedScore);
    const status = statusForScore(finalScore);

    return {
      key: profile.key,
      label: profile.label,
      shortLabel: profile.shortLabel,
      description: profile.description,
      score: finalScore,
      exactScore: round(adjustedScore, 2),
      mainScore: Math.round(baseScore),
      deltaFromMain: round(finalScore - baseScore),
      status: status.status,
      tone: status.tone,
      badge: status.badge,
      action: actionForScore(finalScore, profile.key),
      reason: buildReason(profile, finalScore, baseScore, subSignals, macroPressure, dataConfidencePenalty),
      methodology: 'Profile lens — interpretation layer only; does not override the main signal.',
    };
  });
}

export default computeInstitutionalRiskProfiles;
